/**
 * Perk, trait, skill and stat text and art — out of the player's own install.
 *
 * The editor already had the names, hard-coded from the engine's enums. What it
 * did not have is what the *game* calls them and what the game says they do, and
 * those are not the editor's to invent: they live in the install's `.msg` files,
 * which a mod is free to rewrite. A player looking at "BONUS_HTH_ATTACKS" and a
 * player looking at "Bonus HtH Attacks — you have learned to make an extra
 * attack" are not being told the same thing.
 *
 * ## Where the text is (verified in `engine/src`, not guessed)
 *
 * All four files sit under `game\` — resolved by the localisation layer to
 * `text/<language>/game/…` — and every one uses a base-plus-index convention:
 *
 *   | file       | name        | description  | source                        |
 *   |------------|-------------|--------------|-------------------------------|
 *   | perk.msg   | `101 + id`  | `1101 + id`  | perk.cc `perksInit`           |
 *   | trait.msg  | `100 + id`  | `200 + id`   | trait.cc `traitsInit`         |
 *   | skill.msg  | `100 + id`  | `200 + id`   | skill.cc `skillsInit`         |
 *   | stat.msg   | `100 + id`  | `200 + id`   | stat.cc `statsInit`           |
 *
 * Note the perk description offset is a *thousand*, not a hundred: the perk
 * names run 101–219 and would have collided. skill.msg also carries `300 + id`,
 * the short "attributes" string the character screen shows under a skill — the
 * game's own way of writing which stats govern it, which is better than ours.
 *
 * ## Art
 *
 * Perks, traits, skills and the SPECIAL seven all have icons, and they are all
 * lines in `art/skilldex/skilldex.lst`. The index is *not* positional for perks
 * and traits — `gPerkDescriptions[n].frmId` and `gTraitDescriptions[n].frmId`
 * are hardcoded columns that reuse and reorder ids (two perks share 154, trait
 * 13 is 94 rather than 68). Those columns are in `perktable.ts`, generated from
 * the engine source.
 *
 * Both consumers in the engine build the fid the same way —
 * `buildFid(OBJ_TYPE_SKILLDEX, frmId)`, character_editor.cc:5026 and :6982 —
 * and `ArtResolver` already understands object type 10, so the existing decoder
 * needs nothing new.
 *
 * ## Refusals
 *
 * Every field is optional. An install with no `perk.msg` yields entries with
 * empty text and the editor falls back to the enum name, which is what it showed
 * before. Nothing here throws and nothing here invents a description.
 */

import { getText, parseMsg, type MsgFile } from '../mods/msg-codec';
import {
  PERK_COUNT,
  PERK_NAMES,
  PRIMARY_STAT_COUNT,
  SKILL_COUNT,
  SKILL_NAMES,
  STAT_NAMES,
  TRAIT_COUNT,
  TRAIT_NAMES,
} from '../saveedit/constants';
import { PERK_ROWS, SKILL_FRM_IDS, SPECIAL_FRM_IDS, TRAIT_FRM_IDS, skilldexFid } from '../saveedit/perktable';
import type { GameFiles } from './files';

export interface ChargenEntry {
  /** The engine enum name — always present, and the fallback for `name`. */
  key: string;
  /** What the install calls it, or the enum name when the text is missing. */
  name: string;
  /** What the install says it does. Empty when the text is missing. */
  description: string;
  /** A skilldex fid for `ArtResolver.frameForFid`, or 0 when there is no art. */
  fid: number;
  /** True when `name` came from the install rather than the enum fallback. */
  named: boolean;
}

export interface ChargenText {
  perks: ChargenEntry[];
  traits: ChargenEntry[];
  skills: ChargenEntry[];
  /** The SPECIAL seven only — the derived stats' text is not what players want here. */
  special: ChargenEntry[];
  /** skill.msg `300 + id`: the game's own "AG" / "PE, IN" line per skill. */
  skillAttributes: string[];
  /** Which files answered, for the provenance line. */
  sources: string[];
  /** True when at least one of the four files was found. */
  ok: boolean;
}

const EMPTY: ChargenText = {
  perks: [],
  traits: [],
  skills: [],
  special: [],
  skillAttributes: [],
  sources: [],
  ok: false,
};

/**
 * Where a message file might be, most specific first — the same shape
 * `items.ts` uses, and for the same reason: the engine takes the language from
 * `[system] language` and falls back to English.
 */
const candidates = (file: string, language?: string | null): string[] => [
  ...(language && language.toLowerCase() !== 'english' ? [`text/${language.toLowerCase()}/game/${file}`] : []),
  `text/english/game/${file}`,
];

async function loadMsg(
  files: GameFiles,
  file: string,
  language: string | null | undefined,
): Promise<{ msg: MsgFile; path: string } | null> {
  for (const path of candidates(file, language)) {
    const bytes = await files.read(path);
    if (!bytes) continue;
    try {
      return { msg: parseMsg(bytes), path };
    } catch {
      // A .msg the engine would also reject. Try the next candidate rather than
      // failing the whole load: three good files and one bad one is still worth
      // showing.
    }
  }
  return null;
}

/** One category's entries, given its bases and its art column. */
function entries(
  keys: readonly string[],
  msg: MsgFile | null,
  nameBase: number,
  descriptionBase: number,
  fidFor: (index: number) => number,
): ChargenEntry[] {
  return keys.map((key, index) => {
    const name = msg ? (getText(msg, nameBase + index) ?? '').trim() : '';
    const description = msg ? (getText(msg, descriptionBase + index) ?? '').trim() : '';
    return {
      key,
      name: name || key,
      description,
      fid: fidFor(index),
      named: name.length > 0,
    };
  });
}

/**
 * Read all four. One call, because the save editor's Perks & Traits tab wants
 * three of them at once and the archives are the expensive part.
 */
export async function loadChargenText(
  files: GameFiles,
  language?: string | null,
): Promise<ChargenText> {
  const [perkMsg, traitMsg, skillMsg, statMsg] = await Promise.all([
    loadMsg(files, 'perk.msg', language),
    loadMsg(files, 'trait.msg', language),
    loadMsg(files, 'skill.msg', language),
    loadMsg(files, 'stat.msg', language),
  ]);

  const sources = [perkMsg, traitMsg, skillMsg, statMsg]
    .filter((m): m is { msg: MsgFile; path: string } => m !== null)
    .map((m) => m.path);
  if (!sources.length) return EMPTY;

  // Perk art is the `frmId` column of gPerkDescriptions, which is neither
  // sequential nor unique past the vanilla block — read it, never compute it.
  const perkFid = (index: number) => skilldexFid(PERK_ROWS[index]?.[0] ?? 0);
  const traitFid = (index: number) => skilldexFid(TRAIT_FRM_IDS[index] ?? 0);
  const skillFid = (index: number) => skilldexFid(SKILL_FRM_IDS[index] ?? 0);
  const statFid = (index: number) => skilldexFid(SPECIAL_FRM_IDS[index] ?? 0);

  const skillAttributes = Array.from({ length: SKILL_COUNT }, (_, i) =>
    skillMsg ? (getText(skillMsg.msg, 300 + i) ?? '').trim() : '',
  );

  return {
    perks: entries(PERK_NAMES.slice(0, PERK_COUNT), perkMsg?.msg ?? null, 101, 1101, perkFid),
    traits: entries(TRAIT_NAMES.slice(0, TRAIT_COUNT), traitMsg?.msg ?? null, 100, 200, traitFid),
    skills: entries(SKILL_NAMES.slice(0, SKILL_COUNT), skillMsg?.msg ?? null, 100, 200, skillFid),
    special: entries(STAT_NAMES.slice(0, PRIMARY_STAT_COUNT), statMsg?.msg ?? null, 100, 200, statFid),
    skillAttributes,
    sources,
    ok: true,
  };
}

/** The empty result, for callers that need a value before the read lands. */
export const EMPTY_CHARGEN_TEXT: ChargenText = EMPTY;
